import {
  API_BASE_URL,
  parseIstatecResponse,
  parseValidationError,
  type IstatecResponse,
} from './apiConfig';

/**
 * Which form the message is being polished for. The backend uses this to
 * add a small context hint to Gemini so RFP-tab polish reads slightly
 * more crisp / business-like than the general contact-us polish.
 */
export type ImproveMessageContext = 'contact' | 'rfp';

export interface ImproveMessagePayload {
  text: string;
  context: ImproveMessageContext;
}

/** Shape of the `data` field on a successful response. */
export interface ImproveMessageData extends Record<string, unknown> {
  improved_text: string;
  original_length?: number;
  improved_length?: number;
  context?: ImproveMessageContext;
}

/**
 * POST /improve_message_via_gemini — application/json.
 *
 * Fail-open on the UX layer: any non-success response leaves the caller's
 * text intact and simply surfaces the error message to the user.
 */
export async function improveMessage(
  payload: ImproveMessagePayload,
): Promise<IstatecResponse<ImproveMessageData | null>> {
  try {
    const res = await fetch(`${API_BASE_URL}/improve_message_via_gemini`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    if (res.status === 422) {
      return (await parseValidationError(res)) as IstatecResponse<ImproveMessageData | null>;
    }
    return (await parseIstatecResponse(res)) as IstatecResponse<ImproveMessageData | null>;
  } catch {
    return {
      status: 'error',
      message: 'Network error. Please check your connection and try again.',
      status_code: 0,
      data: null,
      request_id: '',
    };
  }
}
